
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, RotateCcw, FileCheck, FolderKanban, Archive, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import RichTextDisplay from '@/components/RichTextDisplay';

export default function InactiveContractsPage() {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchInactive = async () => {
    try {
      const r = await api.get('/contracts/inactive');
      setContracts(r.data || []);
    } catch {
      toast.error('Failed to load inactive contracts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInactive(); }, []);

  const reactivate = async (cid) => {
    try {
      const r = await api.post(`/contracts/${cid}/reactivate`);
      toast.success('Contract reactivated' + (r.data.reactivated_project ? `. Project ${r.data.reactivated_project.project_number} set to Active` : ''));
      fetchInactive();
    } catch {
      toast.error('Failed to reactivate');
    }
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="inactive-contracts-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/contracts')} data-testid="inactive-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="flex items-baseline gap-3 text-[22px] font-medium tracking-tight">Inactive Contracts<span className="text-sm text-muted-foreground/70 font-normal tabular-nums">{contracts.length}</span></h1>
          </div>
        </div>
      </div>

      <div className="relative max-w-xs">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search by contract #, title, or client..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border w-64" data-testid="inactive-contracts-search" />
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground animate-pulse">Loading...</div>
      ) : (() => {
        const q = search.toLowerCase();
        const filtered = !q ? contracts : contracts.filter(c =>
          (c.contract_number || '').toLowerCase().includes(q)
          || (c.title || '').toLowerCase().includes(q)
          || (c.client_name || '').toLowerCase().includes(q)
        );
        if (filtered.length === 0) {
          return (
            <Card className="border-border bg-card shadow-none">
              <CardContent className="py-12 text-center">
                <Archive className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-sm font-medium text-muted-foreground">{q ? 'No matches' : 'No inactive contracts'}</p>
                <p className="text-xs text-muted-foreground mt-1">{q ? 'Try a different search.' : 'Contracts you deactivate will appear here'}</p>
              </CardContent>
            </Card>
          );
        }
        return (
        <div className="border border-border rounded-md bg-card">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Contract</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Title</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Client</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Linked Project</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Deactivated</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Reason</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(c => (
                <TableRow key={c.id} className="border-border hover:bg-secondary/30" data-testid={`inactive-row-${c.id}`}>
                  <TableCell className="font-medium text-sm">
                    <div className="flex items-center gap-1.5">
                      <FileCheck className="h-3.5 w-3.5 text-muted-foreground" />
                      {c.contract_number}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{c.title}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.client_name || '-'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs bg-zinc-500/10 text-zinc-400 border-zinc-500/20">Inactive</Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {c.project_number ? (
                      <div className="flex items-center gap-1.5">
                        <FolderKanban className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>{c.project_number}</span>
                        {c.project_name && <span className="text-muted-foreground">· {c.project_name}</span>}
                        <Badge variant="outline" className="text-xs ml-1 bg-amber-500/10 text-amber-400 border-amber-500/20">{c.project_status || 'Archived'}</Badge>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {c.deactivated_at ? new Date(c.deactivated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                    {c.deactivated_by_name && <span className="ml-1">by {c.deactivated_by_name}</span>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[200px]">
                    <RichTextDisplay value={c.deactivation_reason || '-'} collapsedHeight={36} data-testid={`reason-${c.id}`} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" className="text-xs h-7 gap-1" onClick={() => reactivate(c.id)} data-testid={`reactivate-${c.id}`}>
                      <RotateCcw className="h-3 w-3" /> Reactivate
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        );
      })()}
    </div>
  );
}
